<template>
  <div>
    <StaticDataTable class="static-table-list stock-pricing-matrix-table" v-if="processedPricingData">
      <template v-slot:header>
        <tr>
          <th>Code</th>
          <th width="20%">{{editing ? 'Alias' : 'Name'}}</th>
          <template v-for="priceGroup in processedPricingData.priceGroupList">
            <th :key="priceGroup.id">{{priceGroup.name}}</th>
          </template>
          <th>Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="(stock, stockIndex) in processedPricingData.stockProcessedPriceList" :key="stock.id">
          <td class="font-weight-bold">
            <router-link :to="`/inventory/stock/${stock.id}`">{{stock.code}}</router-link>
            <span v-if="stock.isPendingUpdate" class="pricing-pending-update">
              &nbsp;<Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
            </span>
          </td>
          <td>
            <span v-if="!editing" :class="stock.alias ? 'font-italic' : ''">{{stock.alias ? stock.alias : stock.name}}</span>
            <FormInputText 
              v-if="editing"
              :ref="`stock-alias-${stock.id}`"
              :value="stock.alias"
              @input="setStockAlias(stock, $event)"
              placeholder="Stock Alias"
            />
          </td>
          <template v-for="(priceGroup, index) in processedPricingData.priceGroupList">
            <td :key="priceGroup.id + '-' + stock.id">
              <template v-if="!editing || stock.isPendingUpdate">{{getGroupPrice(stock, priceGroup.id)}}</template>
              <span v-show="editing && stock.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              <FormInputCurrency 
                v-if="editing && !stock.isPendingUpdate"
                :value="getGroupPriceValue(stock, priceGroup.id)"
                @input="setGroupPriceValue(stock, priceGroup.id, $event)"
                @tab="onPriceTabbed($event, stockIndex, index)"
              />
            </td>
          </template>
          <td>
            <a v-if="editing && !stock.isPendingUpdate" @click.prevent="removeEditingGlassPricing(stock.id)"><i class="far fa-trash-alt"></i></a>
            <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && stock.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
              <DropdownMenuItem :link="`/pricing/requests/${stock.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>

        <tr v-if="editing && processedPricingData.priceGroupList && addingNewStock">
          <td :colspan="2">
            <div class="alert alert-warning" v-if="editingError">
              <i class="far fa-exclamation-triangle"></i>&nbsp;&nbsp;<strong>Error:</strong> {{editingError}}
            </div>
            <FormSearchableSelect ref="newStockInput" :options="lookupStockList" v-model="newStockId" @input="newStockSelected" placeholder="Stock Code" :loading="loading" v-if="addingNewStock" />
          </td>
          <td :colspan="processedPricingData.priceGroupList.length + 1">
            <ActionButton type="cancel" class="btn-xs ml-2" @click="cancelAddingStockItem()"> Cancel</ActionButton>
          </td>
        </tr>
        <tr v-if="editing && processedPricingData.priceGroupList && !addingNewStock">
          <td :colspan="processedPricingData.priceGroupList.length + 3">
            <ActionButton type="edit" class="btn-xs" @click="newStockButtonPressed" v-if="!addingNewStock"> New Stock</ActionButton>
          </td>
        </tr>

      </template>
    </StaticDataTable>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import StaticDataTable from '@/components/table/staticDataTable'
import { InventoryStockTypes, StockPriceTypes } from '@/store/types'

export default {
  name: 'SupplierProcessedPriceList',
  components: {
    StaticDataTable
  },
  props: {
    contactId: Number,
    supplierCurrencyCode: String,
    supplierProcessedPricingData: Object,
    editing: Boolean,
  },
  data() {
    return {
      loading: false,
      stockList: [],
      lookupStockList: [],
      addingNewStock: false,
      newStockId: null,
      processedPricingData: _.cloneDeep(this.$props.supplierProcessedPricingData),
      editingError: null
    }
  },
  watch: {
    supplierProcessedPricingData: function() {
      this.$data.processedPricingData = _.cloneDeep(this.$props.supplierProcessedPricingData)
    }
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
    })
  },
  mounted() {
    this.loadStockList()
  },
  methods: {
    getGroupPriceValue(stock, priceGroupId) {
      var priceInfo = stock.prices.filter(price => price.stockPriceGroupId == priceGroupId)[0]
      if(priceInfo == null)
        return undefined
      return priceInfo.price
    },
    setGroupPriceValue(stock, priceGroupId, value) {
      var priceInfo = stock.prices.filter(price => price.stockPriceGroupId == priceGroupId)[0]
      if(priceInfo == null) {
        if(value != '' && value != null) {
          stock.prices.push({
            price: value,
           stockPriceGroupId: priceGroupId,
            landedCost: 0
          })
        }
      } else {
        priceInfo.price = value
      }
      this.$emit('update', this.$data.processedPricingData)
    },
    getGroupPrice(stock, priceGroupId) {
      var priceInfo = stock.prices.filter(price => price.stockPriceGroupId == priceGroupId)[0]
      if(priceInfo == null)
        return '-'
      return this.$options.filters.formatCurrency(priceInfo.price) + ' ' + (this.supplierCurrencyCode || '')
    },
    getGroupLanding(stock, priceGroupId) {
      var priceInfo = stock.prices.filter(price => price.stockPriceGroupId == priceGroupId)[0]
      if(priceInfo == null)
        return '-'
      return this.$options.filters.formatCurrency(priceInfo.landedCost) + ' ' + this.accountCurrencyCode
    },
    setStockAlias(stock, alias) {
      stock.alias = alias
      this.$emit('update', this.$data.processedPricingData)
    },

    loadStockList() {
      this.loading = true
      this.$apiService.inventoryService.getStockList().then(response => {
        this.$data.stockList = response.data

        this.$data.lookupStockList = this.$data.stockList
          .filter(stock => stock.isActive && stock.type == InventoryStockTypes.glass)
          .map(stock => {
            return {
              value: stock.id,
              name: `${stock.code} | ${stock.name}`
            }
          })
      })
      .finally(() => {
        this.loading = false
      })
    },
    newStockButtonPressed() {
      this.addingNewStock = true
      this.editingError = ''
      this.$nextTick(() => {
        this.$refs.newStockInput.focus()
        this.$refs.newStockInput.select()
      })
    },
    cancelAddingStockItem() {
      this.addingNewStock = false
      this.editingError = ''
      this.newStockId = null
    },
    newStockSelected(stockId) {
      if(this.addNewStockItem(stockId)) {
        this.$nextTick(() => {
          let aliasRef = `stock-alias-${stockId}`
          this.$refs[aliasRef][0].focus()
          this.$refs[aliasRef][0].select()
        })
      }
    },
    addNewStockItem(stockId) {
      this.editingError = null
      if(stockId == null)
        return false

      // Check if the code is already in the list
      if(this.processedPricingData.stockProcessedPriceList.find(stock => stock.id == stockId) != null) {
        this.editingError = 'Stock item is already in the list'
        return false
      }

      // Get stock info from list
      var stockItem = this.$data.stockList.find(stock => stock.id == stockId)
      if(stockItem == null) {
        this.editingError = 'Could not find stock item'
        return false
      }

      // Add an item to the list
      this.processedPricingData.stockProcessedPriceList.push({
        id: stockId,
        code: stockItem.code,
        name: stockItem.name,
        prices: []
      })

      this.$emit('update', this.$data.processedPricingData)

      this.addingNewStock = false
      this.newStockId = null
      return true
    },
    removeEditingGlassPricing(stockId) {
      this.processedPricingData.stockProcessedPriceList = this.processedPricingData.stockProcessedPriceList.filter(stock => stock.id != stockId)
      this.$emit('update', this.$data.processedPricingData)
    },
    onPriceTabbed(evt, stockIndex, priceGroupIndex) {
      // If the last price group & stock item then start a new stock row
      if(stockIndex >= this.processedPricingData.stockProcessedPriceList.length-1 && priceGroupIndex >= this.processedPricingData.priceGroupList.length-1) {
        evt.preventDefault()
        this.newStockButtonPressed()
      }
    }
  }
}
</script>

